import { registerDecorators as _registerDecorators, LightningElement, registerComponent as _registerComponent } from "lwc";
import _tmpl from "./carouselV2.html";
import { createEvent } from '~/modules/analytics/analyticsHelper';
import { CarouselIconAction } from '~/modules/analytics/gtmEventTypes/gtmCarouselClickType';
import { ElementType } from '~/modules/analytics/gtmEventTypes/constants';
import { createUserActivity } from '~/modules/services/simplifiedTrialsService';
import { i18nStore, WireI18n } from '~/modules/stores/i18nStore';
import { generateMediaUrl, isSupportedMediaUrl, MEDIA_ID_PATTERN } from '~/modules/utilities/asset_utils';
const DEFAULT_RAIL_ITEM_COUNT = 3;
// How many steps of preload headroom to keep beyond what is already on screen, in each
// direction. 1 covers a single Next/Previous; raise it to absorb faster consecutive clicks
// through long galleries, at the cost of more speculative image downloads.
const PRELOAD_LOOKAHEAD = 1;
const MAX_PAGINATION_DOTS = 15;
const AUTO_PLAY_INTERVAL_MS = 5000;
const CAROUSEL_ACTIVITY_TYPE = 'Screenshot';
const VIDEO_PREVIEW_SRC = '/images/videoPlaceholderBG.png';
const VIDEO_START_PATTERN = /^\d+$/;
const VIDEO_PROVIDERS = ['YouTube', 'Vidyard', 'Vimeo', 'Wistia'];
/**
 * Publishing stores the id as the partner supplied it, so a YouTube share link's tracking
 * query can ride along on it (`T41OrKHhQN4?si=GD3snekXG0nCp41H`). Kept, it fails
 * MEDIA_ID_PATTERN and the video is dropped from the carousel; encoded into an embed URL
 * it would 404. Only YouTube is trimmed: Vimeo's `?h=` is an unlisted video's access
 * token, part of the address rather than tracking, so trimming it would trade a missing
 * video for a broken player. The trimmed id is still validated before use.
 */
const videoEmbedId = (mediaId, provider) => {
  return provider === 'YouTube' ? mediaId.split(/[?#]/, 1)[0] : mediaId;
};
class CarouselV2 extends LightningElement {
  constructor(...args) {
    super(...args);
    this.labels = void 0;
    this.mediaItems = null;
    this.isMobile = false;
    this.railItemCount = DEFAULT_RAIL_ITEM_COUNT;
    this._autoPlay = false;
    this.selectedItem = 0;
    this.startX = 0;
    this.startY = 0;
    this.showLightbox = false;
    this.initialLightboxItemPosition = 0;
    this.shouldRestoreMediaFocus = false;
    this.didSwipe = false;
    this.autoPlayStoppedByUser = false;
    this.restoreDotFocusAfterSlide = false;
    // Retained preloader Images for the items we are warming, keyed by src. Holding the
    // reference is deliberate: an orphaned in-flight Image can have its download cancelled
    // when it is garbage-collected, reopening the very gap we are trying to close. It also
    // doubles as a dedup key. Bounded to the look-ahead window — items we move away from
    // are evicted.
    this.preloadedMedia = new Map();
  }
  get autoPlay() {
    return this._autoPlay;
  }
  set autoPlay(value) {
    if (value && !this._autoPlay) {
      this.autoPlayStoppedByUser = false;
    }
    this._autoPlay = value;
    this.updateAutoPlay();
  }
  connectedCallback() {
    this.updateAutoPlay();
  }
  disconnectedCallback() {
    this.stopAutoPlay();
  }
  renderedCallback() {
    // Host visibility, not just content: the template renders nothing when no media
    // survives validation, but the host itself still takes a slot in the parent's
    // flex layout and collects its gap.
    const itemCount = this.items.length;
    this.classList.toggle('carousel-v2--empty', itemCount === 0);
    if (this.selectedItem >= itemCount) {
      this.selectedItem = 0;
    }
    this.updateAutoPlay();
    this.preloadAdjacentMedia();
    if (this.shouldRestoreMediaFocus) {
      this.shouldRestoreMediaFocus = false;
      const priorFocus = this.activeElementBeforeLightbox;
      const focusTarget = priorFocus?.isConnected ? priorFocus : this.template.querySelector('[data-testid="carousel-v2-selected-action"]');
      this.activeElementBeforeLightbox = undefined;
      focusTarget?.focus();
    } else if (this.restoreDotFocusAfterSlide) {
      this.restoreDotFocusAfterSlide = false;
      // The slide windowed out the focused dot (its real-index key left the visible
      // slice), so focus dropped to <body> and arrow keys no longer reach the
      // carousel's keydown handler. Only then — activeElement is null — pull focus
      // to the selected dot, which the window always keeps rendered.
      if (!this.template.activeElement) {
        this.template.querySelector('.pagination-dot.selected')?.focus();
      }
    }
  }
  get items() {
    const carouselSourceItems = (this.mediaItems ?? []).filter(item => item.pluginType === 'carousel' && item.data).filter(({
      type,
      data
    }) => {
      return type === 'Image' ? this.isValidImageMedia(data) : this.isValidVideoMedia(data);
    }).sort((a, b) => (a.data?.position ?? 0) - (b.data?.position ?? 0));
    const carouselItems = carouselSourceItems.map((item, index) => {
      const data = item.data;
      const type = item.type === 'Image' ? 'Image' : 'Video';
      return {
        altText: data.caption || i18nStore.getState().i18n.listingDetails.carousel.fallbackAltTag({
          CURRENT_POSITION: index + 1,
          TOTAL_ITEMS: carouselSourceItems.length
        }),
        caption: data.caption ?? '',
        src: type === 'Image' ? generateMediaUrl(data.mediaId ?? '') : this.generateVideoUrl(data.mediaId ?? '', data.videoType, data.videoStart),
        previewSrc: type === 'Image' ? generateMediaUrl(data.mediaId ?? '') : this.videoPreviewUrl(data.mediaId ?? '', data.videoType),
        type
      };
    });
    return carouselItems.map((item, key) => ({
      ...item,
      key
    }));
  }
  get selectedMediaItem() {
    return this.items[this.selectedItem];
  }
  get selectedMediaItems() {
    // The keyed iteration recreates the one-shot analytics instrument for each selected media item.
    return this.selectedMediaItem ? [this.selectedMediaItem] : [];
  }
  get railItems() {
    return this.inactiveItems.slice(0, this.visibleRailItemCount).map(item => ({
      ...item,
      isInteractive: item.type !== 'Image'
    }));
  }
  get selectedMediaAnnouncement() {
    return i18nStore.getState().i18n.listingDetails.carousel.pagination({
      CURRENT_POSITION: this.selectedItem + 1,
      TOTAL_ITEMS: this.items.length
    });
  }
  get hasMultipleItems() {
    return this.items.length > 1;
  }
  get inactiveItems() {
    return [...this.items.slice(this.selectedItem + 1), ...this.items.slice(0, this.selectedItem)];
  }
  get visibleRailItemCount() {
    return Number.isFinite(this.railItemCount) ? Math.max(0, Math.floor(this.railItemCount)) : DEFAULT_RAIL_ITEM_COUNT;
  }
  get mediaRailClass() {
    return this.inactiveItems.length >= this.visibleRailItemCount ? 'media-rail media-rail--filled' : 'media-rail';
  }
  get paginationStyle() {
    // Feeds the count-aware gap in carouselV2.css so the dots keep their 8px spacing
    // when they fit and tighten to stay on one line when the row runs out of room.
    return `--pagination-dot-count: ${Math.min(this.items.length, MAX_PAGINATION_DOTS)}`;
  }
  get paginationItems() {
    const total = this.items.length;
    // Cap the dot count so the row never overflows or wraps. Beyond the cap, slide a
    // fixed-size window that keeps the selected item within it; each dot's `key` stays
    // the real item index, so click-to-select and the active highlight still line up.
    const half = Math.floor(MAX_PAGINATION_DOTS / 2);
    const start = total <= MAX_PAGINATION_DOTS ? 0 : Math.min(Math.max(this.selectedItem - half, 0), total - MAX_PAGINATION_DOTS);
    return this.items.slice(start, start + MAX_PAGINATION_DOTS).map(item => ({
      ...item,
      ariaCurrent: item.key === this.selectedItem ? 'true' : 'false',
      ariaLabel: i18nStore.getState().i18n.listingDetails.carousel.pagination({
        CURRENT_POSITION: item.key + 1,
        TOTAL_ITEMS: total
      }),
      className: item.key === this.selectedItem ? 'pagination-dot selected' : 'pagination-dot'
    }));
  }
  get isSelectedImage() {
    return this.selectedMediaItem?.type === 'Image';
  }
  get isSelectedVideo() {
    return this.selectedMediaItem?.type === 'Video';
  }
  get lightboxItems() {
    // One tile per unique media src — wrapping next/prev selects the first item,
    // it must not appear again as a cloned tile after the last.
    const seen = new Set();
    return this.items.filter(item => {
      const id = item.src || item.previewSrc;
      if (!id || seen.has(id)) {
        return false;
      }
      seen.add(id);
      return true;
    }).map((item, position) => ({
      altText: item.altText,
      caption: item.caption,
      position,
      shouldAutoPlay: item.type === 'Video',
      src: item.src,
      thumb: item.previewSrc,
      type: item.type,
      isVideo: item.type === 'Video'
    }));
  }
  handlePrevious(event) {
    // Only blur on click — keyboard path reuses this handler with the section as currentTarget
    if (event.type === 'click') {
      event.currentTarget.blur();
    }
    this.stopAutoPlayForInteraction();
    this.selectOffset(-1);
  }
  handleNext(event) {
    if (event.type === 'click') {
      event.currentTarget.blur();
    }
    this.stopAutoPlayForInteraction();
    this.selectOffset(1);
  }
  handlePreviousAnalytics(event) {
    event.detail.elementType = ElementType.CAROUSEL;
    event.detail.label = CarouselIconAction.BACK;
  }
  handleNextAnalytics(event) {
    event.detail.elementType = ElementType.CAROUSEL;
    event.detail.label = CarouselIconAction.NEXT;
  }
  handleSelect(event) {
    const index = Number(event.currentTarget.dataset.index);
    if (Number.isInteger(index) && index >= 0 && index < this.items.length) {
      this.activeElementBeforeLightbox = event.currentTarget;
      this.selectedItem = index;
      if (event.currentTarget.classList.contains('pagination-dot')) {
        this.dispatchEvent(createEvent({
          eventType: 'click',
          elementType: ElementType.CAROUSEL,
          label: CarouselIconAction.DOT
        }));
      }
      this.stopAutoPlayForInteraction();
      this.shouldRestoreMediaFocus = true;
    }
  }
  handleKeyDown(event) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.handlePrevious(event);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.handleNext(event);
    }
  }
  handleTouchStart(event) {
    this.didSwipe = false;
    this.startX = event.touches[0]?.clientX ?? 0;
    this.startY = event.touches[0]?.clientY ?? 0;
  }
  handleTouchEnd(event) {
    const horizontalDistance = (event.changedTouches[0]?.clientX ?? this.startX) - this.startX;
    const verticalDistance = (event.changedTouches[0]?.clientY ?? this.startY) - this.startY;
    if (Math.abs(horizontalDistance) >= 40 && Math.abs(horizontalDistance) > Math.abs(verticalDistance)) {
      this.didSwipe = true;
      this.stopAutoPlayForInteraction();
      this.selectOffset(horizontalDistance > 0 ? -1 : 1);
    }
  }
  handleTouchCancel() {
    this.didSwipe = false;
  }
  handleThumbnailKeyDown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.handleSelect(event);
    }
  }
  handleSelectedMediaClick(event) {
    if (this.didSwipe) {
      event.preventDefault();
      this.didSwipe = false;
      return;
    }
    this.openLightbox(this.selectedMediaItem);
  }
  openLightbox(selected) {
    // Match deduped lightbox slides by src (not carousel key / demo-offset index)
    const position = this.lightboxItems.findIndex(item => item.src === selected?.src);
    if (position >= 0) {
      this.activeElementBeforeLightbox = this.template.activeElement;
      this.stopAutoPlayForInteraction();
      this.dispatchEvent(createEvent({
        eventType: 'click',
        elementType: ElementType.CAROUSEL,
        caption: selected?.caption
      }));
      createUserActivity(CAROUSEL_ACTIVITY_TYPE);
      this.initialLightboxItemPosition = position;
      this.showLightbox = true;
    }
  }
  closeLightbox() {
    this.showLightbox = false;
    this.shouldRestoreMediaFocus = true;
  }
  handleImageShown(event) {
    event.detail.image = {
      ...event.detail.image,
      positionIndex: this.selectedItem
    };
  }
  updateAutoPlay() {
    if (this.autoPlay && !this.autoPlayStoppedByUser && this.isConnected && this.items.length > 1 && !(window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false)) {
      if (!this.autoPlayTimer) {
        this.autoPlayTimer = setInterval(() => this.selectOffset(1), AUTO_PLAY_INTERVAL_MS);
      }
      return;
    }
    this.stopAutoPlay();
  }
  stopAutoPlay() {
    if (this.autoPlayTimer) {
      clearInterval(this.autoPlayTimer);
      this.autoPlayTimer = undefined;
    }
  }
  stopAutoPlayForInteraction() {
    this.autoPlayStoppedByUser = true;
    this.stopAutoPlay();
  }
  selectOffset(offset) {
    const itemCount = this.items.length;
    if (itemCount > 0) {
      // A slide can window out the dot that held keyboard focus; note it now so
      // renderedCallback can rescue focus once the new window has rendered.
      const active = this.template?.activeElement;
      this.restoreDotFocusAfterSlide = active?.classList.contains('pagination-dot') ?? false;
      this.selectedItem = (this.selectedItem + offset + itemCount) % itemCount;
    }
  }
  preloadAdjacentMedia() {
    // Warm the browser cache for the items just outside what is already on screen, so a
    // Next or Previous lands on an already-downloaded image instead of the gray
    // placeholder. The rail already downloads the next `visibleRailItemCount` items (its
    // <img>s load even while the rail is display:none on mobile), so the forward gap is
    // the item one past the rail's edge — the thumbnail about to slide into view — not
    // the immediate +1, which the rail already holds. Backward there is no rail buffer at
    // all, so the immediate previous item is the gap. previewSrc is the source the stage
    // renders (image file for images, poster thumbnail for videos). Setting src is enough
    // to prime the cache; degrades gracefully: a failed load falls back to on-demand.
    // With few items the forward offset wraps back onto an already-loaded near item,
    // which is harmless.
    if (typeof Image === 'undefined') {
      return;
    }
    const galleryItems = this.items;
    const itemCount = galleryItems.length;
    if (itemCount < 2) {
      // Nothing to warm, but a gallery that shrank below two items must not keep the
      // prior gallery's preloaded Images around — the eviction loop below is skipped,
      // so drop them here to keep the retained set bounded to the current gallery.
      this.preloadedMedia.clear();
      return;
    }
    const railEdge = Math.min(this.visibleRailItemCount, itemCount - 1);
    // Forward, step past the rail's edge (the rail already holds everything up to it);
    // backward, step from the selection since there is no rail buffer behind it.
    const offsets = [];
    for (let depth = 1; depth <= PRELOAD_LOOKAHEAD; depth++) {
      offsets.push(railEdge + depth, -depth);
    }
    const wanted = new Set();
    for (const offset of offsets) {
      const src = galleryItems[(this.selectedItem + offset + itemCount) % itemCount]?.previewSrc;
      if (!src) {
        continue;
      }
      wanted.add(src);
      if (!this.preloadedMedia.has(src)) {
        const img = new Image();
        // Assigning src starts the download into the HTTP cache; the Image is never
        // added to the DOM.
        img.src = src;
        this.preloadedMedia.set(src, img);
      }
    }
    // Drop items we've moved past so the retained set stays bounded to the look-ahead
    // window. A src that becomes adjacent again is re-primed from the HTTP cache.
    for (const src of this.preloadedMedia.keys()) {
      if (!wanted.has(src)) {
        this.preloadedMedia.delete(src);
      }
    }
  }
  isValidVideoMedia(data) {
    // Provider embed IDs are opaque identifiers, not uploaded asset filenames.
    const embedId = videoEmbedId(data.mediaId ?? '', data.videoType ?? 'YouTube');
    return Boolean(embedId && MEDIA_ID_PATTERN.test(embedId)) && this.isSupportedVideoProvider(data.videoType ?? 'YouTube') && (!data.videoStart || VIDEO_START_PATTERN.test(data.videoStart));
  }
  isValidImageMedia(data) {
    return Boolean(data.mediaId && isSupportedMediaUrl(data.mediaId));
  }
  isSupportedVideoProvider(videoType) {
    return VIDEO_PROVIDERS.includes(videoType);
  }
  generateVideoUrl(mediaId, videoType = 'YouTube', videoStart) {
    const provider = this.isSupportedVideoProvider(videoType) ? videoType : 'YouTube';
    const encodedId = encodeURIComponent(videoEmbedId(mediaId, provider));
    const urls = {
      YouTube: `https://www.youtube-nocookie.com/embed/${encodedId}`,
      Vidyard: `https://play.vidyard.com/${encodedId}`,
      Vimeo: `https://player.vimeo.com/video/${encodedId}`,
      Wistia: `https://fast.wistia.net/embed/iframe/${encodedId}`
    };
    const url = new URL(urls[provider]);
    // Put start before autoplay so query order matches carousel v1 (?start=&autoplay=)
    if (provider === 'YouTube' && videoStart) {
      url.searchParams.set('start', videoStart);
    }
    url.searchParams.set('autoplay', '1');
    return url.toString();
  }
  videoPreviewUrl(mediaId, videoType = 'YouTube') {
    const embedId = videoEmbedId(mediaId, videoType);
    return videoType === 'YouTube' && MEDIA_ID_PATTERN.test(embedId) ? `https://i.ytimg.com/vi/${encodeURIComponent(embedId)}/mqdefault.jpg` : VIDEO_PREVIEW_SRC;
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(CarouselV2, {
  publicProps: {
    mediaItems: {
      config: 0
    },
    isMobile: {
      config: 0
    },
    railItemCount: {
      config: 0
    },
    autoPlay: {
      config: 3
    }
  },
  track: {
    selectedItem: 1,
    startX: 1,
    startY: 1,
    showLightbox: 1,
    initialLightboxItemPosition: 1
  },
  wire: {
    labels: {
      adapter: WireI18n,
      dynamic: [],
      config: function ($cmp) {
        return {
          selector: i18n => i18n.listingDetails.carousel
        };
      }
    }
  },
  fields: ["_autoPlay", "shouldRestoreMediaFocus", "didSwipe", "autoPlayStoppedByUser", "restoreDotFocusAfterSlide", "preloadedMedia"]
});
const __lwc_component_class_internal = _registerComponent(CarouselV2, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;